import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

import { useMemo, useState } from 'react';

import { LineChart } from '@mantine/charts';

import {
  IconArrowRight,
  IconClock,
  IconCoins,
  IconFile,
  IconPlus,
  IconTrendingUp,
  IconUsers,
} from '@tabler/icons-react';

import {
  Badge,
  Button,
  CardView,
  type CompletionRingStatus,
  EmptyStateView,
  Grid,
  Group,
  InlineEditableTextView,
  IntervalCompletionRowView,
  LabeledValueListView,
  NumberInput,
  Progress,
  RingStatView,
  Select,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
  formatHours,
  formatMoney,
} from '@optiro/ui';

import { IntervalCompletionStatePopover } from '@/components/IntervalCompletionStatePopover/IntervalCompletionStatePopover';
import { useResources } from '@/hooks/useResources';
import {
  useCreateTaskDependency,
  useDeleteTaskDependency,
  useTaskDependencies,
} from '@/hooks/useTaskDependencies';
import {
  useTaskEconomyReport,
  useTaskIntervalCompletionStates,
  useTasksByProject,
  useUpdateTask,
} from '@/hooks/useTasks';
import { useIntervalsByAssignment } from '@/modules/Planning/hooks/intervals/useIntervals';
import { ResourcesStack } from '@/modules/Projects/components/Tasks/Resources/Resources';
import { useTaskTimesheetReport } from '@/modules/Timesheets';
import { Interval, Task } from '@/types/types';

type OverviewProps = {
  task: Task;
  onTabChange: (tab: string) => void;
};

// ─── Time summary card ────────────────────────────────────────────────────────

const TimeSummary = ({ task, onNavigate }: { task: Task; onNavigate: () => void }) => {
  const { data: timesheets } = useTaskTimesheetReport(task.id);

  const actualHours = parseFloat(timesheets?.actual_time.total_hours || '0');
  const plannedHours = timesheets?.planned_time.total_hours || 0;
  const progressPct = plannedHours > 0 ? Math.min((actualHours / plannedHours) * 100, 100) : 0;
  const overBudget = actualHours > plannedHours && plannedHours > 0;

  return (
    <CardView
      icon={{ icon: IconClock, color: 'blue' }}
      title="Tid"
      actions={[{ icon: IconArrowRight, label: 'Gå til detaljer', onClick: onNavigate }]}
      loading={!timesheets}
    >
      <Group align="center" gap="lg">
        <RingStatView value={progressPct} color={overBudget ? 'red' : 'blue'} />
        <Stack gap={6} style={{ flex: 1 }}>
          <LabeledValueListView
            items={[
              { label: 'Planlagt', hours: plannedHours },
              {
                label: 'Faktisk',
                value: (
                  <Text size="sm" fw={600} c={overBudget ? 'red' : 'blue'}>
                    {formatHours(actualHours)}
                  </Text>
                ),
              },
            ]}
          />
          <Progress mt={4} value={progressPct} color={overBudget ? 'red' : 'blue'} size="xs" />
          {overBudget && (
            <Badge size="xs" color="red" variant="light">
              Overskredet med {(actualHours - plannedHours).toFixed(1)}t
            </Badge>
          )}
          {plannedHours === 0 && (
            <Text size="xs" c="dimmed" fs="italic">
              Ingen planlagt tid endnu
            </Text>
          )}
        </Stack>
      </Group>
    </CardView>
  );
};

// ─── Economy summary card ─────────────────────────────────────────────────────

const EconomySummary = ({ task, onNavigate }: { task: Task; onNavigate: () => void }) => {
  const { data: report } = useTaskEconomyReport(task.id);

  const hasAnyData = report && (report.estimated_cost !== null || report.actual_cost !== null);

  const progressPct =
    report?.estimated_cost && report.estimated_cost > 0 && report.actual_cost !== null
      ? Math.min((report.actual_cost / report.estimated_cost) * 100, 100)
      : null;

  const overBudget =
    report?.estimated_cost !== null &&
    report?.actual_cost !== null &&
    (report?.actual_cost ?? 0) > (report?.estimated_cost ?? 0);

  return (
    <CardView
      icon={{ icon: IconCoins, color: 'teal' }}
      title="Økonomi"
      actions={[{ icon: IconArrowRight, label: 'Gå til detaljer', onClick: onNavigate }]}
      loading={!report}
    >
      {!hasAnyData ? (
        <EmptyStateView variant="inline" title="Ingen økonomidata endnu" />
      ) : (
        <Stack gap="sm">
          <LabeledValueListView
            items={[
              { label: 'Kostpris', money: report!.estimated_cost },
              { label: 'Salgspris', money: task.latest_sales_price?.price ?? null },
              {
                label: 'Faktisk',
                value:
                  report!.actual_cost !== null ? (
                    <Text size="sm" fw={600} c={overBudget ? 'red' : 'blue'}>
                      {formatMoney(report!.actual_cost)}
                    </Text>
                  ) : (
                    <Text size="sm" c="dimmed" fs="italic">
                      –
                    </Text>
                  ),
              },
            ]}
          />
          {progressPct !== null && (
            <Progress mt={4} value={progressPct} color={overBudget ? 'red' : 'teal'} size="xs" />
          )}
          {report!.expenses.length > 0 && (
            <Text size="xs" c="dimmed" mt={4}>
              {report!.expenses.length} udgift{report!.expenses.length !== 1 ? 'er' : ''}{' '}
              registreret
            </Text>
          )}
        </Stack>
      )}
    </CardView>
  );
};

// ─── Completion section ───────────────────────────────────────────────────────

const RING_STATUS: Record<string, CompletionRingStatus> = {
  pending: 'pending_approval',
  approved: 'approved',
  declined: 'in_progress',
};

const INTERVAL_COLORS = ['blue', 'grape', 'orange', 'cyan', 'pink', 'lime'] as const;

const CompletionSection = ({ task }: { task: Task }) => {
  const { data: states = [] } = useTaskIntervalCompletionStates(task.id);
  const { data: intervals = [] } = useIntervalsByAssignment(task.project_id, task.id);
  const { data: resources = [] } = useResources({ taskId: task.id });

  const intervalIdsWithStates = [...new Set(states.map((s) => s.interval_id))];

  const colorFor = (intervalId: number | string): string => {
    const s = states.find((s) => String(s.interval_id) === String(intervalId));
    const index = intervalIdsWithStates.findIndex((id) => String(id) === String(intervalId));
    return s?.resource_color ?? INTERVAL_COLORS[index % INTERVAL_COLORS.length];
  };

  const latestStateFor = (intervalId: number | string) =>
    [...states]
      .filter((s) => String(s.interval_id) === String(intervalId))
      .sort((a, b) => (a.created_at < b.created_at ? -1 : 1))
      .at(-1);

  const resourceNameFor = (interval: Interval) => {
    if (interval.intervalable_type !== 'Resource') return 'Ikke tildelt';
    const resource = resources.find((r) => String(r.id) === String(interval.intervalable_id));
    return resource?.name ?? 'Ukendt ressource';
  };

  const rows = [...intervals].sort((a, b) => (a.start_date < b.start_date ? -1 : 1));

  const timestamps = [...new Set(states.map((s) => s.created_at))].sort();
  const chartData = timestamps.map((ts) => {
    const row: Record<string, string | number | null> = {
      date: DateTime.fromISO(ts).toFormat('d. MMM HH:mm'),
    };
    for (const id of intervalIdsWithStates) {
      const latest = [...states]
        .filter((s) => s.interval_id === id && s.created_at <= ts)
        .sort((a, b) => (a.created_at < b.created_at ? -1 : 1))
        .at(-1);
      row[`#I-${id}`] = latest?.percentage ?? null;
    }
    return row;
  });
  const series = intervalIdsWithStates.map((id) => ({ name: `#I-${id}`, color: colorFor(id) }));

  return (
    <CardView icon={{ icon: IconTrendingUp, color: 'teal' }} title="Færdiggørelse">
      {rows.length === 0 ? (
        <EmptyStateView variant="inline" title="Ingen intervaller endnu" />
      ) : (
        <Stack gap="md">
          {chartData.length > 1 ? (
            <LineChart
              h={200}
              data={chartData}
              dataKey="date"
              series={series}
              curveType="monotone"
              connectNulls
              withLegend
              withDots={chartData.length < 20}
              yAxisProps={{ domain: [0, 100] }}
            />
          ) : null}
          <Stack gap={0}>
            {rows.map((interval, i) => {
              const latest = latestStateFor(interval.id);
              return (
                <IntervalCompletionStatePopover
                  key={interval.id}
                  intervalId={interval.id}
                  currentPercentage={latest?.percentage ?? 0}
                >
                  {({ onClick, loading }) => (
                    <UnstyledButton onClick={onClick} disabled={loading} display="block" w="100%">
                      <IntervalCompletionRowView
                        bordered={false}
                        interactive
                        withDivider={i < rows.length - 1}
                        intervalId={interval.id}
                        startDate={interval.start_date}
                        endDate={interval.end_date}
                        intervalColor={latest ? colorFor(interval.id) : undefined}
                        percentage={latest?.percentage ?? 0}
                        status={
                          latest ? (RING_STATUS[latest.status] ?? 'in_progress') : 'in_progress'
                        }
                        userName={resourceNameFor(interval)}
                        userSubtitle={
                          latest
                            ? DateTime.fromISO(latest.created_at).toFormat('d. MMM yyyy')
                            : 'Ingen rapport endnu'
                        }
                      />
                    </UnstyledButton>
                  )}
                </IntervalCompletionStatePopover>
              );
            })}
          </Stack>
        </Stack>
      )}
    </CardView>
  );
};

// ─── Dependencies ("Venter på") card ───────────────────────────────────────────

const DependenciesSummary = ({ task }: { task: Task }) => {
  const { t } = useTranslation('task');
  const [adding, setAdding] = useState(false);
  const [predecessorId, setPredecessorId] = useState<string | null>(null);
  const [lagDays, setLagDays] = useState<number | string>(0);
  const [note, setNote] = useState('');

  const { data: dependencies = [] } = useTaskDependencies(task.id);
  const { data: projectTasks = [] } = useTasksByProject(task.project_id ?? undefined);
  const createDependency = useCreateTaskDependency(task.id, task.project_id);
  const deleteDependency = useDeleteTaskDependency(task.id, task.project_id);

  const predecessorTasksById = useMemo(
    () => new Map(projectTasks.map((t) => [String(t.id), t])),
    [projectTasks]
  );

  const availableTasks = useMemo(
    () =>
      projectTasks.filter(
        (t) => t.id !== task.id && !dependencies.some((d) => d.predecessor_task_id === Number(t.id))
      ),
    [projectTasks, dependencies, task.id]
  );

  const resetForm = () => {
    setAdding(false);
    setPredecessorId(null);
    setLagDays(0);
    setNote('');
  };

  const handleAdd = () => {
    if (!predecessorId) return;
    createDependency.mutate(
      {
        predecessor_task_id: Number(predecessorId),
        lag_days: typeof lagDays === 'number' ? lagDays : Number(lagDays) || 0,
        note: note.trim() || undefined,
      },
      { onSuccess: resetForm }
    );
  };

  return (
    <CardView
      icon={{ icon: IconArrowRight, color: 'orange' }}
      title={t('dependencies.title')}
      actions={
        !adding
          ? [{ icon: IconPlus, label: t('dependencies.add'), onClick: () => setAdding(true) }]
          : undefined
      }
    >
      <Stack gap="xs">
        {dependencies.length === 0 && !adding && (
          <EmptyStateView variant="inline" title={t('dependencies.empty')} />
        )}

        {dependencies.length > 0 && (
          <LabeledValueListView
            items={dependencies.map((dependency) => {
              const predecessor = predecessorTasksById.get(String(dependency.predecessor_task_id));
              return {
                label: predecessor?.name ?? `#${dependency.predecessor_task_id}`,
                value:
                  dependency.lag_days !== 0 ? (
                    <Badge size="xs" variant="light" color="orange">
                      {t('dependencies.lag_days_badge', { days: dependency.lag_days })}
                    </Badge>
                  ) : (
                    ''
                  ),
                onRemove: () => deleteDependency.mutate(dependency.id),
              };
            })}
          />
        )}

        {adding && (
          <Stack gap="xs" mt={dependencies.length > 0 ? 'xs' : 0}>
            <Select
              placeholder={t('dependencies.task_placeholder')}
              data={availableTasks.map((t) => ({ value: String(t.id), label: t.name }))}
              value={predecessorId}
              onChange={setPredecessorId}
              searchable
              nothingFoundMessage={t('dependencies.no_other_tasks')}
              size="sm"
            />
            <NumberInput
              label={t('dependencies.lag_days_label')}
              value={lagDays}
              onChange={setLagDays}
              size="sm"
            />
            <TextInput
              placeholder={t('dependencies.note_placeholder')}
              value={note}
              onChange={(e) => setNote(e.currentTarget.value)}
              size="sm"
            />
            <Group gap="xs" justify="flex-end">
              <Button size="xs" variant="subtle" color="gray" onClick={resetForm}>
                {t('dependencies.cancel')}
              </Button>
              <Button
                size="xs"
                onClick={handleAdd}
                loading={createDependency.isPending}
                disabled={!predecessorId}
              >
                {t('dependencies.confirm')}
              </Button>
            </Group>
          </Stack>
        )}
      </Stack>
    </CardView>
  );
};

// ─── Resources card ───────────────────────────────────────────────────────────

const ResourcesSummary = ({ task }: { task: Task }) => (
  <CardView icon={{ icon: IconUsers, color: 'violet' }} title="Ressourcer">
    <ResourcesStack taskId={task.id} />
  </CardView>
);

// ─── Overview ─────────────────────────────────────────────────────────────────

export const Overview = ({ task, onTabChange }: OverviewProps) => {
  const updateTask = useUpdateTask();

  return (
    <Grid gutter="md">
      <Grid.Col span={12}>
        <CardView icon={{ icon: IconFile, color: 'blue' }} title="Beskrivelse">
          <InlineEditableTextView
            value={task.description ?? ''}
            placeholder="Ingen beskrivelse tilgængelig. Klik for at tilføje."
            saving={updateTask.isPending}
            onSave={(description) =>
              updateTask.mutate({ taskId: task.id, updatedTask: { description } })
            }
          />
        </CardView>
      </Grid.Col>
      <Grid.Col span={{ base: 12, sm: 6 }}>
        <TimeSummary task={task} onNavigate={() => onTabChange('time')} />
      </Grid.Col>
      <Grid.Col span={{ base: 12, sm: 6 }}>
        <EconomySummary task={task} onNavigate={() => onTabChange('economy')} />
      </Grid.Col>
      <Grid.Col span={12}>
        <CompletionSection task={task} />
      </Grid.Col>
      <Grid.Col span={12}>
        <ResourcesSummary task={task} />
      </Grid.Col>
      <Grid.Col span={12}>
        <DependenciesSummary task={task} />
      </Grid.Col>
    </Grid>
  );
};
